import { useEffect } from 'react';
import { MapRef } from 'react-map-gl';

// create a canvas with a size of m*m
const createCanvas = (size: number) => {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  return canvas;
};

// draw a rotated stripe pattern - function for the updatePattern function
const drawRotatedStripes = (
  ctx: CanvasRenderingContext2D,
  stripeWidth: number,
  gap: number,
  color: string,
  size: number
) => {
  const tempCanvas = createCanvas(size * 2);
  const tempCtx = tempCanvas.getContext('2d');
  if (!tempCtx) return;

  tempCtx.fillStyle = 'rgba(0,0,0,0)';
  tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
  tempCtx.fillStyle = color;

  const totalWidth = stripeWidth + gap;
  for (let y = 0; y < tempCanvas.height; y += totalWidth) {
    tempCtx.fillRect(0, y, tempCanvas.width, stripeWidth);
  }

  ctx.save();
  ctx.translate(size / 2, size / 2);
  ctx.rotate(Math.PI / 4);
  ctx.drawImage(
    tempCanvas,
    -tempCanvas.width * 0.75,
    -tempCanvas.height * 0.75,
    tempCanvas.width * 1.5,
    tempCanvas.height * 1.5
  );
  ctx.restore();
};

// update the pattern - function for the addPatternImages function
const updatePattern = (
  map: mapboxgl.Map,
  id: string,
  drawFn: (ctx: CanvasRenderingContext2D, size: number) => void
) => {
  const size = 64;
  const canvas = createCanvas(size);
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.clearRect(0, 0, size, size);
  drawFn(ctx, size);

  if (map.hasImage(id)) map.removeImage(id);
  // add the pattern to the map
  map.addImage(
    id,
    {
      data: ctx.getImageData(0, 0, size, size).data,
      width: size,
      height: size,
    },
    { pixelRatio: 1 }
  );
};

// update the diagonal pattern - function for the addPatternImages function
const updateDiagonalPattern = (map: mapboxgl.Map, zoom: number) => {
  let stripeWidth = zoom < 10 ? 2 : 4;
  const gap = zoom < 10 ? 4 : 6;
  const scale = 1;
  // scale the stripe width down by the scale factor
  stripeWidth = stripeWidth / scale;
  updatePattern(map, 'diagonal-hatch-pattern', (ctx, size) => {
    ctx.imageSmoothingEnabled = false;
    drawRotatedStripes(ctx, stripeWidth, gap, '#9F1853', size);
  });
};

// update the line pattern - function for the addPatternImages function
const updateLinePattern = (
  map: mapboxgl.Map,
  id: string,
  color: string,
  zoom: number,
  orientation: 'horizontal' | 'vertical'
) => {
  const lineWidth = zoom < 10 ? 2 : 4;
  const gap = zoom < 10 ? 8 : zoom < 13 ? 12 : 16;

  updatePattern(map, id, (ctx, size) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    for (let i = gap / 2; i < size; i += gap) {
      if (orientation === 'vertical') {
        ctx.moveTo(i, 0);
        ctx.lineTo(i, size);
      } else {
        ctx.moveTo(0, i);
        ctx.lineTo(size, i);
      }
    }
    ctx.stroke();
  });
};

// add the patterns to the map - function for the useReorderEntityLayers function
export const addPatternImages = (map: mapboxgl.Map) => {
  if (!map.hasImage('diagonal-hatch-pattern')) {
    updateDiagonalPattern(map, 12);
  }
  if (!map.hasImage('vertical-lines-pattern')) {
    updateLinePattern(map, 'vertical-lines-pattern', '#4589FF', 12, 'vertical');
  }
  if (!map.hasImage('horizontal-lines-pattern')) {
    updateLinePattern(
      map,
      'horizontal-lines-pattern',
      '#42BE65',
      12,
      'horizontal'
    );
  }
};

// Keep the pattern-image fills in sync with the current zoom level.
// (Layer z-ordering now lives in useEnforceLayerOrder / layer-order.ts.)
export const usePatternImageZoom = (mapRef: MapRef | null) => {
  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return;

    // update the patterns when the zoom changes
    const handleZoom = () => {
      const zoom = map.getZoom();
      updateDiagonalPattern(map, zoom);
      updateLinePattern(
        map,
        'vertical-lines-pattern',
        '#4589FF',
        zoom,
        'vertical'
      );
      updateLinePattern(
        map,
        'horizontal-lines-pattern',
        '#42BE65',
        zoom,
        'horizontal'
      );
    };

    map.on('zoom', handleZoom);
    // updatePattern() calls map.hasImage(), which dereferences map.style — and
    // map.style is undefined until the style loads. Under React 18 this effect
    // can run before the style is ready, so only paint once it is, and (re)paint
    // on style (re)load. Mirrors the guarded image setup in map.tsx.
    if (map.isStyleLoaded()) handleZoom();
    map.on('style.load', handleZoom);

    return () => {
      map.off('zoom', handleZoom);
      map.off('style.load', handleZoom);
    };
  }, [mapRef]);
};
